import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Skills del bot (agentic/skills/ en el backend) para el menú "/" del
// chat y los chips azules del área de texto.
//
// - catalog: lo que el bot ofrece. Llega como event "luca/skills" en
//   respuesta al event "luca/listSkills" que App.tsx manda al conectar.
// - pending: la skill que el usuario eligió en el menú y todavía no se
//   envía; viaja en channelData.skill del siguiente mensaje (ver
//   skillsState.ts) y ahí pasa a "active".
// - active: las skills que el bot reporta activas en cada respuesta
//   (channelData.activeSkills). El bot es la fuente de verdad: si la
//   suelta (el usuario cambió de tema, la canceló), el chip desaparece.
// - mine: la creó este usuario con /new-skill (guardada en Cosmos; sólo
//   él la ve). Son las que lista la sección Skills del menú lateral.
// - deleting / deleteError: borrado en curso desde la × de la sección
//   Skills (event "luca/deleteSkill"); el bot contesta con "luca/skills"
//   y el catálogo ya sin esa skill.
export interface SkillInfo {
  name: string;
  command: string;
  title: string;
  summary: string;
  description?: string;
  commandOnly: boolean;
  mine?: boolean;
}

interface SkillsState {
  catalog: SkillInfo[];
  catalogLoaded: boolean;
  pending: SkillInfo | null;
  active: string[];
  deleting: string | null;
  deleteError: string;
}

export interface SkillDeleteResult {
  name: string;
  ok: boolean;
  error?: string;
}

const initialState: SkillsState = {
  catalog: [],
  catalogLoaded: false,
  pending: null,
  active: [],
  deleting: null,
  deleteError: "",
};

const skillsSlice = createSlice({
  name: "skills",
  initialState,
  reducers: {
    setSkillCatalog: (state, action: PayloadAction<SkillInfo[]>) => {
      state.catalog = Array.isArray(action.payload) ? action.payload : [];
      state.catalogLoaded = true;
    },
    startDeleteSkill: (state, action: PayloadAction<string>) => {
      state.deleting = action.payload;
      state.deleteError = "";
    },
    // Respuesta del bot a luca/deleteSkill. Si se borró, también sale
    // de los chips (activa o pendiente): ya no existe en el bot.
    finishDeleteSkill: (state, action: PayloadAction<SkillDeleteResult>) => {
      const { name, ok, error } = action.payload;
      state.deleting = null;
      if (ok) {
        state.active = state.active.filter((n) => n !== name);
        if (state.pending?.name === name) state.pending = null;
      } else if (error) {
        state.deleteError = `Could not delete "${name}". Please try again.`;
      }
    },
    clearDeleteSkillError: (state) => {
      state.deleting = null;
      state.deleteError = "";
    },
    selectSkill: (state, action: PayloadAction<SkillInfo>) => {
      state.pending = action.payload;
    },
    clearPendingSkill: (state) => {
      state.pending = null;
    },
    // El mensaje con channelData.skill ya salió: el chip se queda como
    // activo hasta que la respuesta del bot confirme (o no) la skill.
    markPendingSkillSent: (state) => {
      if (state.pending) {
        const name = state.pending.name;
        state.active = [name, ...state.active.filter((n) => n !== name)];
        state.pending = null;
      }
    },
    syncActiveSkills: (state, action: PayloadAction<string[]>) => {
      state.active = Array.isArray(action.payload)
        ? action.payload.map(String)
        : [];
    },
    removeActiveSkill: (state, action: PayloadAction<string>) => {
      state.active = state.active.filter((name) => name !== action.payload);
    },
    // Conversación nueva: sin chips (el catálogo se conserva).
    resetSkills: (state) => {
      state.pending = null;
      state.active = [];
    },
  },
});

export const {
  setSkillCatalog,
  startDeleteSkill,
  finishDeleteSkill,
  clearDeleteSkillError,
  selectSkill,
  clearPendingSkill,
  markPendingSkillSent,
  syncActiveSkills,
  removeActiveSkill,
  resetSkills,
} = skillsSlice.actions;
export default skillsSlice.reducer;
